export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

export const COLLECTIONS_TOOLS = [
  {
    name: 'send_request',
    description:
      'Send an HTTP request and return the response. Use use_current_tab:true to resend/repeat the active Request Builder tab without opening a new tab. Use request_id to open a saved request. Otherwise provide method+url (interactive mode opens Request Builder).',
    input_schema: {
      type: 'object',
      properties: {
        method: { type: 'string', enum: HTTP_METHODS },
        url: { type: 'string' },
        headers: { type: 'object', additionalProperties: { type: 'string' } },
        body: { type: 'string' },
        request_id: {
          type: 'string',
          description:
            'Optional saved request ID — opens that request in Request Builder when interactive mode is on',
        },
        use_current_tab: {
          type: 'boolean',
          description:
            'When true, send the active Request Builder tab (resend/repeat). Use when user says resend, again, same tab, or run in same window. method/url not needed.',
        },
      },
      required: [],
    },
  },
  {
    name: 'get_collections',
    description: 'Get all saved API collections and their requests.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'get_environments',
    description: 'Get all environments and their variables (secrets are masked).',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'get_request',
    description: 'Get a specific saved request by ID: method, url, type (http/grpc/websocket/sse), the auth it is really sent with and where that comes from (request, a folder, the collection, or none — types and {{variable}} names only), and protocol fields for gRPC/WebSocket/SSE.',
    input_schema: {
      type: 'object',
      properties: { request_id: { type: 'string' } },
      required: ['request_id'],
    },
  },
  {
    name: 'set_variable',
    description: 'Set an environment or global variable.',
    input_schema: {
      type: 'object',
      properties: {
        key: { type: 'string' },
        value: { type: 'string' },
        scope: { type: 'string', enum: ['environment', 'global'] },
      },
      required: ['key', 'value'],
    },
  },
  {
    name: 'get_workspace_variables',
    description: 'Get workspace-level variables.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'get_global_variables',
    description: 'Get global variables.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'get_current_request',
    description:
      'Get the active Request Builder tab (method, url, tab_id, saved_request_id, type, auth, protocol). Works for unsaved draft tabs — not only sidebar-selected saved requests. type is http/grpc/websocket/sse; auth says which auth is really sent and where it comes from (request, folder, collection or none) — types and {{variable}} names only, never a credential; protocol carries the gRPC/WebSocket/SSE fields.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'create_collection',
    description:
      'Create a new API collection. Returns collection id — use it for create_folders and create_request in follow-up tool calls. When the user asks for collection + folder + request, run all three steps; do not stop after only creating the collection.',
    input_schema: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'Collection name' },
      },
      required: ['name'],
    },
  },
  // Batch pattern D0 (22.08.2026): the plural REPLACES the singular. One call
  // for any number of folders — one folder included — so "a folder per
  // resource" is one round trip, not N turns each carrying every tool schema.
  {
    name: 'create_folders',
    description:
      'Create one or more folders in a collection in ONE call — use it for every folder request, including a single folder; never call it once per folder. Each item needs a name; a name with slashes ("Contacts/Lists") is a path and missing ancestors are created. Items run in order, so a later item may nest under an earlier one by path, or under an existing folder via parent_folder_id. Existing folders with the same name and parent are reused (existed:true), never duplicated. Returns a folder_id per item for create_request / move_requests; items that fail are reported individually and the rest are still created. Max 50 items.',
    input_schema: {
      type: 'object',
      properties: {
        collection_id: { type: 'string' },
        folders: {
          type: 'array',
          description: 'Up to 50 items, created in order.',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string', description: 'Folder name, or a slash path like "Billing/Invoices".' },
              parent_folder_id: {
                type: 'string',
                description: 'Optional existing folder to nest under (the name/path is relative to it).',
              },
              path: {
                type: 'string',
                description: 'Optional ancestor path like "Billing/2026" to create the folder under; missing ancestors are created.',
              },
            },
            required: ['name'],
          },
        },
      },
      required: ['collection_id', 'folders'],
    },
  },
  {
    name: 'create_request',
    description:
      'Create and save a new request in a collection. Pass folder_id when the request belongs in a folder. Use Apiboo variable syntax {{variableName}} in URLs/headers (not OpenAPI {variableName}). Random User API: https://randomuser.me/api',
    input_schema: {
      type: 'object',
      properties: {
        collection_id: { type: 'string' },
        name: { type: 'string' },
        method: { type: 'string', enum: HTTP_METHODS },
        url: { type: 'string' },
        folder_id: { type: 'string', description: 'Optional folder id' },
        headers: { type: 'object', additionalProperties: { type: 'string' } },
        body: { type: 'string' },
      },
      required: ['collection_id', 'method', 'url'],
    },
  },
  {
    name: 'move_requests',
    description:
      'Move existing requests into folders within a collection. Use get_collections first to get request ids and folder ids. Batch multiple moves in one call when reorganizing (e.g. group Deals, Contacts, Tasks into separate folders). Omit folder_id or pass null to move to collection root.',
    input_schema: {
      type: 'object',
      properties: {
        collection_id: { type: 'string' },
        moves: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              request_id: { type: 'string' },
              folder_id: {
                type: 'string',
                description: 'Target folder id; omit for collection root',
              },
            },
            required: ['request_id'],
          },
        },
      },
      required: ['collection_id', 'moves'],
    },
  },
  {
    name: 'run_collection',
    description:
      'Run all requests in a collection sequentially. In interactive mode, opens the Run Collection modal and auto-runs there. Use get_collections first if you only have a name. Returns per-request status and pass/fail summary.',
    input_schema: {
      type: 'object',
      properties: {
        collection_id: { type: 'string' },
        collection_name: {
          type: 'string',
          description: 'Collection name — use when user says run collection X',
        },
        folder_id: {
          type: 'string',
          description: 'Optional — run only requests inside this folder subtree',
        },
      },
      required: [],
    },
  },
  {
    name: 'get_last_response',
    description:
      'Read the last captured response for a request (status, headers, and the response BODY, truncated). send_request only surfaces status — call this when you need to inspect the body, e.g. before writing tests. Reads the active Request Builder tab unless request_id is given. Errors if no response has been captured yet (send the request first).',
    input_schema: {
      type: 'object',
      properties: {
        request_id: {
          type: 'string',
          description: 'Optional saved request ID — defaults to the active request tab',
        },
      },
      required: [],
    },
  },
  {
    name: 'generate_tests',
    description:
      "Write a test script onto a request's Tests tab and run it against the STORED response (no re-send). Provide test_script using the pm.* API only: pm.test(name, fn), pm.expect(value), pm.response.to.have.status(code), pm.response.code, pm.response.json(), pm.response.headers.get(name). Call get_last_response first so your assertions match the real body. The script is written as UNSAVED for the user to review and save. Returns pass/fail and the previous script for a diff.",
    input_schema: {
      type: 'object',
      properties: {
        test_script: {
          type: 'string',
          description: 'The pm.* test script to write and run against the stored response',
        },
        request_id: {
          type: 'string',
          description: 'Optional saved request ID — defaults to the active request tab',
        },
      },
      required: ['test_script'],
    },
  },
  {
    name: 'generate_pre_request_script',
    description:
      "Write a PRE-REQUEST script onto a request's Pre-request tab and dry-run it. Use this — not generate_tests — whenever the user asks for a pre-request, setup, or before-send script (timestamps, nonces, signatures, computing a token). There is NO response in a pre-request context: pm.response does not exist, and pm.test has nothing to assert against (a pm.test that throws is reported in failed_tests and fails the dry run). Available API: pm.environment.get/set/unset/has, pm.variables.get/set/has/unset, pm.variables.replaceIn('{{tpl}}'), pm.collectionVariables and pm.globals (aliases of pm.environment), read-only pm.request.url (variables already substituted) and pm.request.method, pm.info.requestName and pm.info.requestId, pm.console.log. A pre-request script CANNOT read the request's own headers: pm.request.headers is EMPTY before send, so headers.get() always returns null — never write a script that depends on it. Scripts also cannot add or change headers; to inject a computed value, pm.environment.set('sig', ...) and tell the user to reference {{sig}} in the request's Headers tab. pm.sendRequest is not supported. The script is written UNSAVED for the user to review and save. The DRY RUN changes nothing: it reports whether the script compiled and ran, plus the NAMES (never the values) of the variables it would set — real writes happen only when the user sends the request. IMPORTANT — secrets are WITHHELD during validation: every variable the user marked secret reads as unavailable and throws in the dry run, INCLUDING one with 'Allow script access' enabled, because the dry run only needs to know whether the script compiles. So a signing/HMAC script that reads a secret will report a 'marked secret and is not available to scripts' error here and still be CORRECT at send time. Do not rewrite the script to dodge that error — say so to the user and move on. A '<redacted>' inside a dry-run error is a scrubbed secret value and may occasionally stand in for an ordinary word.",
    input_schema: {
      type: 'object',
      properties: {
        pre_request_script: {
          type: 'string',
          description: 'The pm.* pre-request script to write and dry-run',
        },
        request_id: {
          type: 'string',
          description: 'Optional saved request ID — defaults to the active request tab',
        },
      },
      required: ['pre_request_script'],
    },
  },
  {
    name: 'update_request',
    description:
      'Apply a narrow patch to a saved request (name, method, url, headers, body). Use this to fix a request after diagnosing a failing run — change only what needs changing; omit fields you are not touching. Always shows a Confirm card to the user before applying (even in Auto-run). Not for creating requests (use create_request) or bulk edits.',
    input_schema: {
      type: 'object',
      properties: {
        request_id: { type: 'string' },
        name: { type: 'string' },
        method: { type: 'string', enum: HTTP_METHODS },
        url: { type: 'string' },
        headers: { type: 'object', additionalProperties: { type: 'string' } },
        body: { type: 'string' },
      },
      required: ['request_id'],
    },
  },
];
